// TKOS custom icon set — every icon hand-drawn to a single visual rhythm.
//
// Spec, applied without exception:
//   • viewBox: 0 0 24 24
//   • stroke-width: 1.5 (via .tkos-icon class — do not duplicate per-path)
//   • stroke-linecap: round, stroke-linejoin: round (via .tkos-icon class)
//   • stroke: currentColor — caller controls colour by setting CSS color
//   • fill: none — outline icons only, no filled shapes
//
// Add new icons here and only here. If a component needs an icon that doesn't
// exist yet, draw it to this spec — never reach for an icon library.

export type IconName =
  // chrome
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-up'
  | 'check'
  | 'close'
  | 'plus'
  | 'minus'
  | 'search'
  | 'arrow-up'
  | 'arrow-down'
  | 'arrow-right'
  // status
  | 'alert-triangle'
  | 'info'
  | 'success-circle'
  | 'pulse-dot'
  // domain — kitchen retail
  | 'customer'
  | 'kitchen'
  | 'pound'
  | 'calendar'
  | 'truck'
  | 'sparkle'
  | 'bell'
  | 'send'
  | 'eye'
  | 'eye-off'
  | 'pencil'
  | 'trash'
  | 'refresh'
  | 'sort'
  | 'empty-pipeline'
  // chrome — navigation + actions
  | 'menu'
  | 'fullscreen'
  | 'fullscreen-exit'
  | 'log-out'
  | 'moon'
  | 'sun'
  | 'dashboard'
  | 'chart'
  | 'card'
  | 'package'
  | 'image'
  | 'file'
  | 'folder'
  | 'gear'
  | 'help'
  | 'link'
  | 'palette'
  | 'team'
  | 'chat'
  | 'lightbulb'
  | 'star'
  | 'cloud'
  | 'music'
  | 'note'
  | 'tag'
  | 'filter'
  | 'phone'
  | 'mail'
  | 'apps'
  | 'ruler'
  | 'hammer'
  | 'upload'
  | 'download'
  | 'play'
  | 'pin'
  | 'signature'
  | 'paperclip'
  | 'copy'
  | 'cloud-sun'
  | 'cloud-moon'
  | 'cloud-rain'
  | 'cloud-snow'
  | 'cloud-fog'
  | 'cloud-bolt';

// Each entry is the SVG <path> / shape contents only. The wrapper <svg>,
// viewBox, and stroke styling are added centrally by renderIcon().
const PATHS: Record<IconName, string> = {
  // ── chrome ───────────────────────────────────────────────────────────
  'chevron-down':  '<path d="M6 9l6 6 6-6" />',
  'chevron-up':    '<path d="M6 15l6-6 6 6" />',
  'chevron-left':  '<path d="M15 6l-6 6 6 6" />',
  'chevron-right': '<path d="M9 6l6 6-6 6" />',

  check: '<path d="M5 12.5l4.5 4.5L19 7.5" />',

  close: '<path d="M6 6l12 12M18 6L6 18" />',

  plus:  '<path d="M12 5v14M5 12h14" />',
  minus: '<path d="M5 12h14" />',

  search:
    '<circle cx="11" cy="11" r="6.5" /><path d="M20 20l-3.5-3.5" />',

  'arrow-up':    '<path d="M12 19V5M6 11l6-6 6 6" />',
  'arrow-down':  '<path d="M12 5v14M6 13l6 6 6-6" />',
  'arrow-right': '<path d="M5 12h14M13 6l6 6-6 6" />',

  // ── status ──────────────────────────────────────────────────────────
  'alert-triangle':
    '<path d="M12 4l9 16H3z" /><path d="M12 10v5" /><circle cx="12" cy="18" r="0.6" fill="currentColor" stroke="none" />',

  info:
    '<circle cx="12" cy="12" r="8.5" /><path d="M12 11v5" /><circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none" />',

  'success-circle':
    '<circle cx="12" cy="12" r="8.5" /><path d="M8 12.5l2.8 2.8L16.5 9.5" />',

  // Solid dot — used inside StatusBadge for active states (paired with
  // a CSS pulse animation). 4px diameter at the 24-grid centre.
  'pulse-dot':
    '<circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />',

  // ── domain — kitchen retail ─────────────────────────────────────────
  // customer — shoulders + head, slightly wider than a generic user icon
  // so it reads as a person, not an account chip
  customer:
    '<circle cx="12" cy="8.5" r="3.5" /><path d="M5 19.5c1.4-3.8 4.1-5.5 7-5.5s5.6 1.7 7 5.5" />',

  // kitchen — stylised cabinet front (two doors, base unit, handle)
  // Stands in for "showroom / unit / range" anywhere in the platform.
  kitchen:
    '<path d="M4 4.5h16v15H4z" /><path d="M12 4.5v15M4 16h16" /><path d="M10 10v2.5M14 10v2.5" />',

  // pound — bespoke £ glyph drawn at icon weight (not the font character)
  pound:
    '<path d="M15.5 7.5c-.7-1.2-2-2-3.5-2-2.2 0-4 1.8-4 4v2.5H6.5" /><path d="M6.5 14h9" /><path d="M8 17.5c0-1.5-.5-2.6-1-3.5h8.5" /><path d="M6.5 11.5h5.5" />',

  // calendar — header strip + body grid hint via two notches at top
  calendar:
    '<rect x="4" y="5.5" width="16" height="14.5" rx="1.5" /><path d="M4 10h16" /><path d="M8.5 3.5v4M15.5 3.5v4" />',

  // truck — used for deliveries / dispatch / driver pages
  truck:
    '<path d="M3.5 6h11v10h-11z" /><path d="M14.5 9.5h4l2 3v3.5h-6" /><circle cx="7.5" cy="17.5" r="1.6" /><circle cx="17" cy="17.5" r="1.6" />',

  // sparkle — used for AI moments (Kit, Kit-Q, Customer AI prompts) and
  // for the "confetti" pulse on success. Four-point starburst — diamond
  // body with four rays — readable at small sizes.
  sparkle:
    '<path d="M12 4v4M12 16v4M4 12h4M16 12h4" /><path d="M12 8.5l1.6 1.9L15.5 12l-1.9 1.6L12 15.5l-1.6-1.9L8.5 12l1.9-1.6z" />',

  // bell — notifications. Clapper as a soft curve to avoid a triangle
  // collision with the alert icon.
  bell:
    '<path d="M6 17h12l-1.5-2V11a4.5 4.5 0 1 0-9 0v4z" /><path d="M10.5 20a1.5 1.5 0 0 0 3 0" />',

  // send — paper-plane drawn as a single closed outline
  send:
    '<path d="M21 4L3 11l7 2 2 7z" /><path d="M21 4l-11 9" />',

  // eye — "view / preview" actions, NOT used for password reveal in this
  // build (we don't have a password reveal pattern yet)
  eye:
    '<path d="M2.5 12C5 7.5 8.5 5.5 12 5.5s7 2 9.5 6.5c-2.5 4.5-6 6.5-9.5 6.5s-7-2-9.5-6.5z" /><circle cx="12" cy="12" r="2.5" />',

  // eye-off — "hidden / private" companion to eye. Same shape with a
  // diagonal slash. Used in Designs page for the per-file
  // hidden-from-customer toggle.
  'eye-off':
    '<path d="M3.5 6.5l17 11" /><path d="M9.9 5.6A9.1 9.1 0 0 1 12 5.5c3.5 0 7 2 9.5 6.5a14.5 14.5 0 0 1-2.2 3.1" /><path d="M14.1 18.4A9.1 9.1 0 0 1 12 18.5c-3.5 0-7-2-9.5-6.5A14.5 14.5 0 0 1 5.6 6" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />',

  // pencil — "edit" action. Diagonal pen body with a small writing nib.
  pencil:
    '<path d="M4 20l4-1 11-11-3-3L5 16z" /><path d="M14.5 5.5l3 3" />',

  // trash — "delete" action. Lid + body with two inner strokes.
  trash:
    '<path d="M4 7h16" /><path d="M9 7V5.5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5.5V7" /><path d="M6 7l1 12.5a1.5 1.5 0 0 0 1.5 1.5h7a1.5 1.5 0 0 0 1.5-1.5L18 7" /><path d="M10 11v6M14 11v6" />',

  // refresh — circular arrow. Used for sync, retry, "pull to refresh".
  refresh:
    '<path d="M4 12a8 8 0 0 1 13.7-5.7L20 8.5" /><path d="M20 4v4.5h-4.5" /><path d="M20 12a8 8 0 0 1-13.7 5.7L4 15.5" /><path d="M4 19.5V15h4.5" />',

  // sort — used as the table-column sort indicator. Two stacked chevrons
  // with the active direction set via CSS opacity (parent flips a class).
  sort:
    '<path d="M8 10l4-4 4 4" /><path d="M8 14l4 4 4-4" />',

  // empty-pipeline — empty-state illustration for the CRM pipeline.
  // Diagonal track with a single placeholder card "drifting in" — a hint
  // that something is missing rather than a literal "no data" symbol.
  'empty-pipeline':
    '<path d="M3.5 18l7-12" /><path d="M13 5.5h7.5v5H13z" /><path d="M16 8h2" />',

  // ── chrome — navigation + actions ────────────────────────────────────
  // Three even lines — slightly narrower than the platform default so the
  // hamburger reads as "menu" rather than a stack.
  menu:
    '<path d="M4 7h16M4 12h16M4 17h16" />',

  fullscreen:
    '<path d="M4 9V4h5" /><path d="M20 9V4h-5" /><path d="M4 15v5h5" /><path d="M20 15v5h-5" />',

  'fullscreen-exit':
    '<path d="M9 4v5H4" /><path d="M15 4v5h5" /><path d="M9 20v-5H4" /><path d="M15 20v-5h5" />',

  'log-out':
    '<path d="M14 3h5v18h-5" /><path d="M3 12h13" /><path d="M11 7l5 5-5 5" />',

  moon:
    '<path d="M20.5 14a8.5 8.5 0 1 1-10.5-10.5 7 7 0 0 0 10.5 10.5z" />',

  sun:
    '<circle cx="12" cy="12" r="3.5" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" />',

  // dashboard — four panes with one slightly larger to read as "tiles"
  // rather than a literal 2×2 grid
  dashboard:
    '<rect x="3.5" y="3.5" width="7"  height="9"  rx="1.5" /><rect x="13.5" y="3.5" width="7" height="5"  rx="1.5" /><rect x="3.5" y="15.5" width="7" height="5"  rx="1.5" /><rect x="13.5" y="11.5" width="7" height="9" rx="1.5" />',

  // chart — three rising bars
  chart:
    '<path d="M5 20V12" /><path d="M12 20V6" /><path d="M19 20v-5" /><path d="M3.5 20h17" />',

  // card — payment / billing
  card:
    '<rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10.5h18" /><path d="M7 15h3" />',

  // package / box — suppliers / inventory
  package:
    '<path d="M3 7.5l9-4 9 4" /><path d="M3 7.5v9l9 4 9-4v-9" /><path d="M3 7.5l9 4 9-4" /><path d="M12 11.5V20" />',

  // image — a photograph. Frame, sun, and a hill breaking the lower edge, so it
  // reads as a picture rather than the generic `file` at small sizes.
  image:
    '<rect x="3" y="4.5" width="18" height="15" rx="2" /><circle cx="8.5" cy="10" r="1.5" /><path d="M3.5 17l4.5-4.5 3.5 3.5 3-3 6 6" />',

  // file — generic document (quotes, designs, submissions)
  file:
    '<path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9 14h6M9 17h4" />',

  // folder — catalogue
  folder:
    '<path d="M3 6.5a1.5 1.5 0 0 1 1.5-1.5h4.5l2 2.5h7.5a1.5 1.5 0 0 1 1.5 1.5V18a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 18z" />',

  // gear — settings / operations / configuration. Six rectangular teeth on
  // a circular body, plus an inner bore circle. The teeth read as distinct
  // protrusions at 24px so the icon doesn't get confused with the sun glyph.
  // Geometry: body radius 6.5, tooth peak radius 8.5, each tooth spans 25°
  // of arc with 35° body gaps between (6 teeth × 60° pitch).
  gear:
    '<path d="M10.59 5.65L10.16 3.7L13.84 3.7L13.41 5.65A6.5 6.5 0 0 1 16.79 7.61L18.27 6.26L20.11 9.44L18.2 10.05A6.5 6.5 0 0 1 18.2 13.95L20.11 14.56L18.27 17.74L16.79 16.39A6.5 6.5 0 0 1 13.41 18.35L13.84 20.3L10.16 20.3L10.59 18.35A6.5 6.5 0 0 1 7.21 16.39L5.73 17.74L3.89 14.56L5.8 13.95A6.5 6.5 0 0 1 5.8 10.05L3.89 9.44L5.73 6.26L7.21 7.61A6.5 6.5 0 0 1 10.59 5.65Z" /><circle cx="12" cy="12" r="2.5" />',

  // help — circle with a soft "?" curve and dot
  help:
    '<circle cx="12" cy="12" r="8.5" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-1 .4-1.5 1-1.5 2.2" /><circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />',

  // link — integrations
  link:
    '<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 1 0-5.7-5.7L11 7" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 1 0 5.7 5.7L13 17" />',

  // palette — branding
  palette:
    '<path d="M12 3a9 9 0 1 0 0 18c1 0 1.5-1 1.5-2s-.5-2 .5-2H17a4 4 0 0 0 4-4 9 9 0 0 0-9-10z" /><circle cx="7.5" cy="11" r="1" fill="currentColor" stroke="none" /><circle cx="10.5" cy="7.5" r="1" fill="currentColor" stroke="none" /><circle cx="15.5" cy="8" r="1" fill="currentColor" stroke="none" /><circle cx="17.5" cy="12" r="1" fill="currentColor" stroke="none" />',

  // team — three figures: two heads + shoulders, one offset behind
  team:
    '<circle cx="9" cy="8" r="3" /><path d="M3.5 19.5c.8-3 3-4.5 5.5-4.5s4.7 1.5 5.5 4.5" /><circle cx="16" cy="9" r="2.5" /><path d="M16 14.5c2 0 3.6 1.1 4.5 3.5" />',

  // chat — speech bubble with two short message ticks
  chat:
    '<path d="M21 12.5a8.5 8 0 1 1-3.2-6.3L21 5l-.7 3.2" /><path d="M8 11h6M8 14h4" />',

  // lightbulb — feedback / suggestions
  lightbulb:
    '<path d="M9 17v.5a3 3 0 0 0 6 0V17" /><path d="M8.5 14.5A5 5 0 1 1 17 11a5 5 0 0 1-1.5 3.5z" /><path d="M10 21h4" />',

  // star — review widget
  star:
    '<path d="M12 3.5l2.6 5.4 5.9.6-4.4 4 1.3 5.8L12 16.5l-5.4 2.8 1.3-5.8-4.4-4 5.9-.6z" />',

  // cloud — weather widget base
  cloud:
    '<path d="M7 18a4 4 0 0 1-.7-7.9 6 6 0 0 1 11.4 1.2A4 4 0 0 1 17 18z" />',

  // music — radio widget / podcasts
  music:
    '<path d="M9 18V6l11-2v12" /><circle cx="7" cy="18" r="2.5" /><circle cx="18" cy="16" r="2.5" />',

  // note — quick notes / to-do widget
  note:
    '<path d="M6 3h9l4 4v14H6z" /><path d="M15 3v4h4" /><path d="M9 12h7M9 16h5" />',

  // tag — labels / categories
  tag:
    '<path d="M3 11V4h7l10 10-7 7z" /><circle cx="7" cy="8" r="1.25" fill="currentColor" stroke="none" />',

  // filter — funnel
  filter:
    '<path d="M3 5h18l-7 9v6l-4-2v-4z" />',

  // phone — telephony / Twilio
  phone:
    '<path d="M5 4.5h3l1.5 4-2 1.2a12 12 0 0 0 6 6l1.2-2 4 1.5v3a2 2 0 0 1-2.2 2A17 17 0 0 1 3 6.7 2 2 0 0 1 5 4.5z" />',

  // mail — email
  mail:
    '<rect x="3" y="5.5" width="18" height="13" rx="2" /><path d="M3.5 7l8.5 6 8.5-6" />',

  // apps — 3x3 dot launcher
  apps:
    '<circle cx="6"  cy="6"  r="1.4" fill="currentColor" stroke="none" /><circle cx="12" cy="6"  r="1.4" fill="currentColor" stroke="none" /><circle cx="18" cy="6"  r="1.4" fill="currentColor" stroke="none" /><circle cx="6"  cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="18" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="6"  cy="18" r="1.4" fill="currentColor" stroke="none" /><circle cx="12" cy="18" r="1.4" fill="currentColor" stroke="none" /><circle cx="18" cy="18" r="1.4" fill="currentColor" stroke="none" />',

  // ruler — bulk-width / dimensions tools in Quote Builder. Diagonal ruler
  // body with three measurement notches at the 1/4, 1/2 and 3/4 marks.
  ruler:
    '<path d="M3.5 14.5l11-11 6 6-11 11z" /><path d="M7 11l1.5 1.5M10 8l1.5 1.5M13 5l1.5 1.5" />',

  // hammer — "installs / fitting" indicator on the Operations dashboard.
  // Hammer head as a tilted rectangle joined to a diagonal handle, drawn
  // to the same 1.5px-stroke / rounded-cap visual rhythm as the rest of
  // the set.
  hammer:
    '<path d="M14 4l6 6-3 3-6-6 3-3z" /><path d="M11 7l-7 7 3 3 7-7" /><path d="M5 17l-2 4 4-2" />',

  // upload — tray with upward arrow. Distinct from `arrow-up` (which is a
  // bare arrow) — used for "drop a file here" / "import CSV".
  upload:
    '<path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16" /><path d="M8 8l4-4 4 4" /><path d="M12 4v12" />',

  // download — tray with downward arrow (mirror of `upload`). Used for the
  // image lightbox "save" action.
  download:
    '<path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16" /><path d="M8 12l4 4 4-4" /><path d="M12 4v12" />',

  // play — media playback (podcast / radio widget on the dashboard)
  play:
    '<path d="M7 4.5v15l13-7.5z" />',

  // pin — map / location pin (used by the live courier tracking widget on
  // the dashboard's "where are my deliveries" card title)
  pin:
    '<path d="M12 3a7 7 0 0 0-7 7c0 5 7 11 7 11s7-6 7-11a7 7 0 0 0-7-7z" /><circle cx="12" cy="10" r="2.5" />',

  // signature — handwritten approval / sign-off. A flowing curve with a
  // baseline underneath. Used by the Quote Builder approval tool.
  signature:
    '<path d="M3 16c3 0 4-9 7-9s3 9 6 9 5-2 5-2" /><path d="M3 20h18" />',

  // paperclip — attach file (messages composer). Two looping curves with a
  // diagonal so it reads as "clip" not "hook".
  paperclip:
    '<path d="M20.5 11.5l-8.5 8.5a5 5 0 1 1-7-7L15 3.5a3.5 3.5 0 1 1 5 5l-9 9a2 2 0 1 1-2.8-2.8L15 8" />',

  // copy — duplicate / copy-to-clipboard. Two offset rectangles, the back
  // one peeking out behind the front to read as "stacked".
  copy:
    '<rect x="9" y="9" width="11" height="11" rx="1.5" /><path d="M5 15V5a1.5 1.5 0 0 1 1.5-1.5H16" />',

  // ── weather (dashboard widget) ───────────────────────────────────────
  // All `cloud-*` variants reuse the same cloud silhouette so the family
  // reads as one weather set.

  'cloud-sun':
    '<circle cx="16.5" cy="7" r="2.5" /><path d="M16.5 1.5v1.5M16.5 11v1.5M11 7h1.5M20.5 7H22M13 3.5l1 1M19 9.5l1 1M13 10.5l1-1M19 4.5l1-1" /><path d="M6 19a3.5 3.5 0 0 1-.6-6.95 5 5 0 0 1 9.6 1.45A3.5 3.5 0 0 1 14.5 19z" />',

  'cloud-moon':
    '<path d="M19 8a4 4 0 1 1-4-5 3 3 0 0 0 4 5z" /><path d="M6 19a3.5 3.5 0 0 1-.6-6.95 5 5 0 0 1 9.6 1.45A3.5 3.5 0 0 1 14.5 19z" />',

  'cloud-rain':
    '<path d="M6 14a3.5 3.5 0 0 1-.6-6.95 5 5 0 0 1 9.6 1.45A3.5 3.5 0 0 1 14.5 14z" /><path d="M8 18v3M12 17v4M16 18v3" />',

  'cloud-snow':
    '<path d="M6 14a3.5 3.5 0 0 1-.6-6.95 5 5 0 0 1 9.6 1.45A3.5 3.5 0 0 1 14.5 14z" /><circle cx="8"  cy="19" r="0.6" fill="currentColor" stroke="none" /><circle cx="12" cy="20" r="0.6" fill="currentColor" stroke="none" /><circle cx="16" cy="19" r="0.6" fill="currentColor" stroke="none" />',

  'cloud-fog':
    '<path d="M6 11a3.5 3.5 0 0 1-.6-6.95 5 5 0 0 1 9.6 1.45A3.5 3.5 0 0 1 14.5 11z" /><path d="M4 15h16M5 19h14" />',

  'cloud-bolt':
    '<path d="M6 14a3.5 3.5 0 0 1-.6-6.95 5 5 0 0 1 9.6 1.45A3.5 3.5 0 0 1 14.5 14z" /><path d="M13 14l-3 5h3l-1 3 4-5h-3z" />',
};

export function iconPath(name: IconName): string {
  const p = PATHS[name];
  if (!p) {
    // Dev-time guard — surface missing icons loudly. In prod this would
    // never fire because IconName is a closed union.
    if (typeof console !== 'undefined') {
      console.warn(`[tkos-icon] unknown icon "${name}"`);
    }
    return '';
  }
  return p;
}

export function listIcons(): IconName[] {
  return Object.keys(PATHS) as IconName[];
}
